import React from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiHash,
  FiHome,
  FiUsers,
  FiX,
  FiZap,
} from "react-icons/fi";
import { DEFAULT_CHARGES, MIN_AMOUNT } from "../../../constants/vending";
import { CopyButton, Field, Notice, PrimaryButton, groupToken, naira, titleCase } from "../../landing/ui";
import {
  confirmOpenVendingPayment,
  createOpenVendingSession,
  getOpenVendingMeter,
} from "../../../api-calls/guest/openVending";

const POLL_SECONDS = 30; // backend allows one confirm call per 30s

const unwrap = (res) => res?.data ?? res;
const errorText = (error) =>
  error?.response?.data?.message || error?.message || "Something went wrong";

// First present field of an unknown-shaped payload, matched case/underscore-insensitively.
const pick = (obj, keys) => {
  if (!obj || typeof obj !== "object") return undefined;
  const norm = (k) => k.toLowerCase().replace(/[^a-z0-9]/g, "");
  const wanted = keys.map(norm);
  const hit = Object.keys(obj).find((k) => wanted.includes(norm(k)) && obj[k]);
  return hit ? obj[hit] : undefined;
};

const formatTime = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// Seconds left until `expiredAt`.
const secondsUntil = (s) => {
  const at = pick(s, ["expiredAt", "expiresAt", "expiryDate", "expiry"]);
  const t = typeof at === "number" ? at : Date.parse(at);
  return Number.isNaN(t) || !at ? 30 * 60 : Math.max(0, Math.round((t - Date.now()) / 1000));
};

// Brand-coloured monograms (not official logos) so the bank is recognisable at a glance.
const BANKS = [
  ["opay", "#00B876", "O"],
  ["palmpay", "#6C2BD9", "P"],
  ["moniepoint", "#0359FF", "M"],
  ["kuda", "#40196D", "K"],
  ["gtb", "#E8590C", "GT"],
  ["guaranty", "#E8590C", "GT"],
  ["access", "#F26B21", "A"],
  ["zenith", "#D11F2E", "Z"],
  ["united bank", "#D32F2F", "U"],
  ["uba", "#D32F2F", "U"],
  ["first bank", "#0B3C75", "F"],
  ["wema", "#8B1D78", "W"],
  ["sterling", "#E4002B", "S"],
  ["fidelity", "#0098DA", "F"],
  ["stanbic", "#0033A0", "S"],
  ["union", "#00AEEF", "U"],
  ["polaris", "#6A1B9A", "P"],
];

function BankBadge({ name, size = 44 }) {
  const lower = String(name || "").toLowerCase();
  const hit = BANKS.find(([k]) => lower.includes(k));
  const color = hit ? hit[1] : "#010623";
  const letters = hit ? hit[2] : (lower[0] || "B").toUpperCase();
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-2xl font-extrabold text-white shadow-sm"
      style={{ width: size, height: size, background: color, fontSize: size * 0.4 }}
    >
      {letters}
    </span>
  );
}

export default function OpenVendingModal({ meterId, onClose }) {
  const [step, setStep] = React.useState("details"); // details | pay | result
  const [meter, setMeter] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");
  const [isFetching, setIsFetching] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [form, setForm] = React.useState({ name: "", email: "", amount: "" });
  const [errors, setErrors] = React.useState({});
  const [session, setSession] = React.useState(null);
  const [secondsLeft, setSecondsLeft] = React.useState(0);
  const [isChecking, setIsChecking] = React.useState(false);
  const [result, setResult] = React.useState(null); // { token, data, message, isError }
  const [cooldown, setCooldown] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    getOpenVendingMeter(meterId)
      .then((res) => !cancelled && setMeter(unwrap(res)))
      .catch((e) => !cancelled && setLoadError(errorText(e)))
      .finally(() => !cancelled && setIsFetching(false));
    return () => {
      cancelled = true;
    };
  }, [meterId]);

  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Expiry countdown (stops once a token is delivered).
  React.useEffect(() => {
    if (result?.token || !session || secondsLeft <= 0) return;
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [session, secondsLeft, result]);

  // Confirm-call cooldown.
  React.useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const setField = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: "" }));
  };

  const minAmount = Math.max(MIN_AMOUNT, Number(meter?.minimumTransfer) || 0);
  const amountTooLow = form.amount !== "" && Number(form.amount) < minAmount;
  // Charge comes from the API when it provides one, otherwise the default.
  const upfrontCharges =
    Number(pick(meter, ["charges", "chargePerTransfer", "transferCharge", "charge"])) || DEFAULT_CHARGES;
  const typedAmount = Number(form.amount);
  const canPreview = typedAmount >= minAmount;
  const totalToPay = typedAmount + upfrontCharges;

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email";
    if (!(Number(form.amount) > 0)) next.amount = "Enter a valid amount";
    else if (Number(form.amount) < minAmount)
      next.amount = `You can't buy below ${naira(minAmount)}`;
    setErrors(next);
    if (Object.keys(next).length) return;

    setIsSubmitting(true);
    createOpenVendingSession({
      meterId,
      name: form.name.trim(),
      email: form.email.trim(),
      amount: Number(form.amount),
    })
      .then((res) => {
        const data = unwrap(res);
        setSession(data);
        setSecondsLeft(secondsUntil(data));
        setStep("pay");
      })
      .catch((err) => toast.error(errorText(err)))
      .finally(() => setIsSubmitting(false));
  };

  const handleConfirm = () => {
    if (cooldown > 0 || isChecking) return;
    setIsChecking(true);
    setCooldown(POLL_SECONDS);
    const sessionId = pick(session, ["sessionId", "id", "reference", "session"]);
    confirmOpenVendingPayment(sessionId)
      .then((res) => {
        const data = unwrap(res);
        const token =
          data?.rechargeData?.token ||
          pick(data, ["token", "meterToken", "electricityToken"]);
        setResult({ token, data, message: res?.message || data?.message, isError: false });
        setStep("result");
      })
      .catch((err) => {
        // Always surface what the API said, keep the button.
        setResult({ data: unwrap(err?.response?.data), message: errorText(err), isError: true });
        setStep("result");
      })
      .finally(() => setIsChecking(false));
  };

  const data = result?.data && !Array.isArray(result.data) ? result.data : {};
  const info = { ...session, ...data };
  const token = result?.token;
  const amountToSend = Number(pick(session, ["amountToSend"])) || 0;
  const amountReceived = Number(data.amountReceived) || 0;
  const fullyReceived = amountReceived > 0 && amountReceived >= amountToSend && !token;
  const partlyReceived = amountReceived > 0 && amountReceived < amountToSend;
  const expired = secondsLeft <= 0 && !token && !fullyReceived;

  // Once the money has arrived but there's no token yet, keep checking automatically.
  React.useEffect(() => {
    if (step === "result" && fullyReceived && cooldown === 0 && !isChecking) handleConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, fullyReceived, cooldown, isChecking]);

  const accountNumber = pick(info, ["accountNumber", "account_number", "nuban"]);
  const bankName = pick(info, ["bankName", "bank", "bank_name"]);
  const accountName = pick(info, ["accountName", "account_name"]);
  const charges = Number(pick(session, ["charges", "chargePerTransfer"])) || 0;
  const tokenAmount = Number(pick(session, ["tokenAmount"])) || 0;
  const failed = result?.isError || data.status === "failed";

  // ----- pieces -----
  const AccountCard = (
    <div className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <BankBadge name={bankName} />
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#6C6F7F]">Bank</p>
          <p className="truncate text-lg font-bold capitalize text-primary-dark">{bankName}</p>
        </div>
      </div>
      <div className="mt-4 rounded-xl bg-[#F3F3F7] px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#6C6F7F]">Account number</p>
        <div className="mt-1 flex items-center justify-between gap-3">
          <p className="text-[28px] font-extrabold leading-none tracking-[0.08em] text-primary-dark">
            {accountNumber}
          </p>
          <CopyButton value={accountNumber} />
        </div>
        {accountName && (
          <p className="mt-2 text-sm font-medium capitalize text-[#4a4e63]">{accountName}</p>
        )}
      </div>
    </div>
  );

  const AmountHero = (
    <div className="text-center">
      <p className="text-sm font-semibold text-[#6C6F7F]">Transfer exactly or above</p>
      <p className="text-[40px] font-extrabold leading-tight tracking-tight text-primary-dark">
        {naira(amountToSend)}
      </p>
      {charges > 0 && tokenAmount > 0 && (
        <p className="text-sm text-[#6C6F7F]">
          {naira(tokenAmount)} electricity + {naira(charges)} transfer charge
        </p>
      )}
    </div>
  );

  const ExpiryPill = (
    <div
      className={`mx-auto inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold ${
        expired ? "bg-red-50 text-red-700" : "bg-[#F3F3F7] text-primary-dark"
      }`}
    >
      <FiClock />
      {expired ? "Session expired" : `Expires in ${formatTime(secondsLeft)}`}
    </div>
  );

  const NeverBelow = (
    <Notice tone="amber" icon={FiAlertTriangle} title="Don't send less">
      Send exactly {amountToSend ? naira(amountToSend) : "the amount shown"} or above. Anything below
      the amount shown won't be accepted, and the minimum purchase is {naira(minAmount)}.
    </Notice>
  );

  const messageNotice = result?.message && (
    <Notice tone={failed ? "red" : token ? "green" : fullyReceived ? "blue" : "amber"}>
      {result.message}
    </Notice>
  );

  const title =
    step === "details"
      ? "Buy electricity"
      : token
      ? "Payment complete"
      : step === "pay"
      ? "Pay with transfer"
      : "Confirming payment";

  const modal = (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-[#010623]/60 backdrop-blur-sm sm:items-center sm:p-6"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <style>{`@keyframes ovUp{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}`}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ animation: "ovUp .25s ease-out" }}
        className="flex max-h-[94vh] w-full flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:max-w-[440px] sm:rounded-[28px]"
      >
        {/* header */}
        <div className="flex items-center justify-between gap-3 border-b border-black/5 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-dark text-secondary-dark">
              <FiZap />
            </span>
            <div className="min-w-0">
              <p className="truncate text-base font-bold text-primary-dark">{title}</p>
              <p className="truncate text-xs text-[#6C6F7F]">
                Meter {meterId}
                {meter?.propertyUnit ? ` · ${titleCase(meter.propertyUnit)}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F3F3F7] text-primary-dark hover:bg-[#E6E6EC]"
          >
            <FiX />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {step === "details" && (
            <>
              {isFetching && (
                <div className="flex items-center justify-center gap-3 py-12 text-[#6C6F7F]">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary-dark/20 border-t-primary-dark" />
                  Verifying meter…
                </div>
              )}
              {loadError && (
                <div className="space-y-4">
                  <Notice tone="red">{loadError}</Notice>
                  <PrimaryButton onClick={onClose}>Close</PrimaryButton>
                </div>
              )}
              {meter && (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="overflow-hidden rounded-2xl border border-black/5 bg-[#F8F8FC]">
                    <div className="flex items-center justify-between gap-3 border-b border-black/5 bg-white px-4 py-3.5">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-dark text-secondary-dark">
                          <FiHash />
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[#6C6F7F]">Meter ID</p>
                          <p className="truncate text-xl font-extrabold leading-tight tracking-wide text-primary-dark">
                            {meter.meterId}
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700">
                        <FiCheckCircle /> Verified
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-px bg-black/5">
                      {meter.propertyUnit && (
                        <div className="col-span-2 flex items-start gap-3 bg-[#F8F8FC] px-4 py-3">
                          <FiHome className="mt-0.5 shrink-0 text-[#6C6F7F]" />
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-[#6C6F7F]">Property</p>
                            <p className="font-semibold text-primary-dark">{titleCase(meter.propertyUnit)}</p>
                          </div>
                        </div>
                      )}
                      {meter.communityName && (
                        <div className="flex items-start gap-3 bg-[#F8F8FC] px-4 py-3">
                          <FiUsers className="mt-0.5 shrink-0 text-[#6C6F7F]" />
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-[#6C6F7F]">Community</p>
                            <p className="font-semibold text-primary-dark">{titleCase(meter.communityName)}</p>
                          </div>
                        </div>
                      )}
                      <div className={`flex items-start gap-3 bg-[#F8F8FC] px-4 py-3 ${meter.communityName ? "" : "col-span-2"}`}>
                        <FiCreditCard className="mt-0.5 shrink-0 text-[#6C6F7F]" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[#6C6F7F]">Outstanding bills</p>
                          <p className={`font-semibold ${Number(meter.billsToPay) > 0 ? "text-red-600" : "text-green-700"}`}>
                            {naira(meter.billsToPay ?? 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Field label="Full name" placeholder="Your name" value={form.name}
                    onChange={setField("name")} error={errors.name} disabled={isSubmitting} />
                  <Field label="Email" type="email" placeholder="you@example.com" value={form.email}
                    onChange={setField("email")} error={errors.email} disabled={isSubmitting} />
                  <Field label="Amount" prefix="₦" type="number" inputMode="numeric" min={minAmount}
                    placeholder={`${minAmount} minimum`} value={form.amount}
                    onChange={setField("amount")} disabled={isSubmitting}
                    error={errors.amount || (amountTooLow ? `You can't buy below ${naira(minAmount)}` : "")} />

                  {canPreview && (
                    <div className="flex flex-col gap-1 rounded-2xl bg-[#F3F3F7] p-4 text-sm">
                      <div className="flex justify-between"><span className="text-[#6C6F7F]">Electricity</span><span className="font-semibold">{naira(typedAmount)}</span></div>
                      <div className="flex justify-between"><span className="text-[#6C6F7F]">Transfer charge</span><span className="font-semibold">{naira(upfrontCharges)}</span></div>
                      <div className="mt-1 flex justify-between border-t border-black/10 pt-2 text-base font-bold text-primary-dark"><span>You will pay</span><span>{naira(totalToPay)}</span></div>
                    </div>
                  )}

                  <Notice tone="amber" icon={FiAlertTriangle} title={`Minimum purchase is ${naira(minAmount)}`}>
                    You can't buy below {naira(minAmount)}. A {naira(upfrontCharges)} transfer charge is added
                    on top of the amount you enter.
                  </Notice>

                  <PrimaryButton type="submit" loading={isSubmitting} disabled={amountTooLow || isSubmitting}>
                    {canPreview ? `Pay ${naira(totalToPay)}` : "Continue"}
                  </PrimaryButton>
                </form>
              )}
            </>
          )}

          {step === "pay" && (
            <div className="flex flex-col gap-4">
              {AmountHero}
              {AccountCard}
              {ExpiryPill}
              {NeverBelow}
            </div>
          )}

          {step === "result" && (
            <div className="flex flex-col gap-4">
              {token && (
                <div className="flex flex-col items-center gap-2 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-3xl text-green-600">
                    <FiCheckCircle />
                  </span>
                  <p className="text-lg font-bold text-primary-dark">Your token is ready</p>
                </div>
              )}

              {messageNotice}

              {token ? (
                <>
                  {/* No account number here: the payment is done and must not be sent to again. */}
                  <div className="rounded-2xl bg-primary-dark p-5 text-center text-white">
                    <p className="text-xs font-semibold uppercase tracking-wider text-white/60">Electricity token</p>
                    <p className="mt-2 break-words text-[26px] font-extrabold leading-tight tracking-[0.12em]">
                      {groupToken(token)}
                    </p>
                    <div className="mt-4 flex justify-center">
                      <CopyButton value={String(token).replace(/\D/g, "")} label="Copy token" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 rounded-2xl bg-[#F3F3F7] p-4 text-sm">
                    {data.rechargeData?.unit != null && (
                      <div>
                        <p className="text-[#6C6F7F]">Units</p>
                        <p className="font-bold">{data.rechargeData.unit} kWh</p>
                      </div>
                    )}
                    {data.tokenAmount != null && (
                      <div>
                        <p className="text-[#6C6F7F]">Amount</p>
                        <p className="font-bold">{naira(data.tokenAmount)}</p>
                      </div>
                    )}
                    <div className="col-span-2">
                      <p className="text-[#6C6F7F]">Meter</p>
                      <p className="font-bold">{info.meterId || meterId}</p>
                    </div>
                  </div>
                  <PrimaryButton onClick={onClose}>Done</PrimaryButton>
                </>
              ) : fullyReceived ? (
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <span className="h-10 w-10 animate-spin rounded-full border-4 border-primary-dark/15 border-t-primary-dark" />
                  <p className="text-sm font-medium text-[#4a4e63]">
                    Payment received. Checking again in {cooldown}s so your token shows automatically.
                  </p>
                </div>
              ) : (
                <>
                  {AmountHero}
                  {AccountCard}
                  {partlyReceived ? (
                    <Notice tone="red" icon={FiAlertTriangle} title="You sent less than the amount shown">
                      Send the balance so the total is exactly {naira(amountToSend)} or above. Anything
                      below the amount shown can't complete your purchase.
                    </Notice>
                  ) : (
                    <Notice tone="amber" icon={FiAlertTriangle} title="Not below the amount shown">
                      Only exactly {naira(amountToSend)} or above works. If you sent less than that, the
                      purchase won't complete.
                    </Notice>
                  )}
                  {ExpiryPill}
                </>
              )}
            </div>
          )}
        </div>

        {/* footer action (pay / still awaiting) */}
        {(step === "pay" || (step === "result" && !token && !fullyReceived)) && (
          <div className="border-t border-black/5 px-5 py-4">
            <PrimaryButton onClick={handleConfirm} loading={isChecking} disabled={expired || cooldown > 0 || isChecking}>
              {"I've sent the money" + (cooldown > 0 ? ` (${cooldown}s)` : "")}
            </PrimaryButton>
          </div>
        )}
        <p className="border-t border-black/5 bg-[#FAFAFD] py-2.5 text-center text-[11px] text-[#8b8fa3]">
          Secured by PrepaidMeter.ng · Minimum purchase {naira(minAmount)}
        </p>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.getElementById("modal") || document.body);
}
